import { useRef, useState } from 'react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import { Loader2, QrCode as QrCodeIcon } from 'lucide-react';
import { CompanyEmployee } from '@durma-soft/gros-sdk/dist/types/company';
import { useEnsureRestaurantCompanyEmployeeQrCodesMutation } from '@durma-soft/gros-sdk';

import { Button } from '@/components/shared/shadcn-ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shared/shadcn-ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/shared/shadcn-ui/tooltip';
import { EmployeeQrCardsPrint } from '@/components/restaurant/companies/employee-qr-cards-print';
import { EmployeeQrBusinessCardsPrint } from '@/components/restaurant/companies/employee-qr-business-cards-print';

import print from '@/lib/print/print';
import { cn, noop } from '@/utils/helpers';

type QrPrintFormat = 'business-card' | 'grid';

const PREVIEW_QR_VALUE = '01HZXPREVIEWTOKEN0000000000';
const PREVIEW_NAME = 'Marko Marković';
const PREVIEW_ID = '#123';
const PREVIEW_USERNAME = 'marko.markovic@firma';

interface FormatOptionCardProps {
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}

const FormatOptionCard = ({
  label,
  isSelected,
  onSelect,
  children,
}: FormatOptionCardProps) => {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={onSelect}
      className="flex flex-col items-center gap-2 rounded-lg p-1"
    >
      {children}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
};

interface PrintEmployeeQrCardsModalContentProps {
  employees: CompanyEmployee[];
  format: QrPrintFormat;
  onFormatChange: (format: QrPrintFormat) => void;
  isGenerating: boolean;
  generatedTokens: Record<string, string>;
}

/** Lives inside DialogContent so the preview QR codes are only rendered while
    the modal is actually open, not once per row in the table */
const PrintEmployeeQrCardsModalContent = ({
  employees,
  format,
  onFormatChange,
  isGenerating,
  generatedTokens,
}: PrintEmployeeQrCardsModalContentProps) => {
  const { t } = useTranslation();

  // With a single employee the preview shows their real card; with many it
  // stays a generic format preview since the printout covers all of them
  const singleEmployee = employees.length === 1 ? employees[0] : null;
  const previewName = singleEmployee
    ? `${singleEmployee.first_name} ${singleEmployee.last_name}`
    : PREVIEW_NAME;
  const previewId = singleEmployee ? `#${singleEmployee.id}` : PREVIEW_ID;
  const previewUsername = singleEmployee
    ? singleEmployee.username
    : PREVIEW_USERNAME;
  const previewQrValue = singleEmployee
    ? (singleEmployee.qr_code_token ?? generatedTokens[singleEmployee.id])
    : PREVIEW_QR_VALUE;

  return (
    <div className="flex flex-col gap-4">
      <div
        role="radiogroup"
        aria-label={t('employee.print-format')}
        className="grid grid-cols-1 gap-3"
      >
        <FormatOptionCard
          label={t('employee.print-format-business-card')}
          isSelected={format === 'business-card'}
          onSelect={() => onFormatChange('business-card')}
        >
          {/* Standard business card: QR left, name + ID + username right */}
          <div
            className={cn(
              'w-full max-w-sm aspect-[85/55] flex items-center justify-center gap-4 rounded-sm border bg-white p-4 overflow-hidden',
              format === 'business-card' ? 'border-primary' : 'border-gray-300',
            )}
          >
            {previewQrValue ? (
              <QRCode value={previewQrValue} className="w-36 h-36 shrink-0" />
            ) : (
              <div className="w-36 h-36 shrink-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {/* The app's global.css sets `* { flex-shrink: 0 }`, so the
                text column must opt back in to shrinking or long names
                overflow the card instead of wrapping */}
            <div className="min-w-0 shrink text-left">
              <p className="text-[15px] font-semibold text-gray-900 break-words">
                {previewName}
              </p>
              <p className="text-[13px] text-gray-700">{previewId}</p>
              <p className="text-xs text-gray-500 break-words">
                {previewUsername}
              </p>
            </div>
          </div>
        </FormatOptionCard>
        <FormatOptionCard
          label={t('employee.print-format-grid')}
          isSelected={format === 'grid'}
          onSelect={() => onFormatChange('grid')}
        >
          {/* Classic card: QR on top, name below */}
          <div
            className={cn(
              'w-full max-w-sm aspect-[85/55] flex flex-col items-center justify-center gap-1 rounded-sm border bg-white p-2 overflow-hidden',
              format === 'grid' ? 'border-primary' : 'border-gray-300',
            )}
          >
            {previewQrValue ? (
              <QRCode value={previewQrValue} className="h-[60%] w-auto" />
            ) : (
              <div className="h-[60%] aspect-square flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
            <p className="text-sm font-semibold text-gray-900 truncate max-w-full">
              {previewName} ({previewId})
            </p>
          </div>
        </FormatOptionCard>
      </div>

      {isGenerating && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          {/* global.css sets `* { flex-shrink: 0 }` - without shrink the
              text refuses to fit the modal width and cannot wrap */}
          <span className="min-w-0 shrink">
            {t('employee.print-qr-codes-tooltip')}
          </span>
        </div>
      )}
    </div>
  );
};

interface PrintEmployeeQrCardsButtonProps {
  employees: CompanyEmployee[] | undefined;
  restaurantId: number;
  companyId: number;
  disabled?: boolean;
  /** 'toolbar' is the labeled button above the table; 'icon' is the compact
      ghost button used inside a table row for a single employee */
  trigger?: 'toolbar' | 'icon';
}

export const PrintEmployeeQrCardsButton = ({
  employees,
  restaurantId,
  companyId,
  disabled = false,
  trigger = 'toolbar',
}: PrintEmployeeQrCardsButtonProps) => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [format, setFormat] = useState<QrPrintFormat>('business-card');
  const [isPrinting, setIsPrinting] = useState(false);
  const [generatedTokens, setGeneratedTokens] = useState<
    Record<string, string>
  >({});

  // Snapshot of the rows shown at the moment the modal was opened, so the
  // printed set stays stable even if the table refetches while it is open
  const snapshotRef = useRef<CompanyEmployee[]>([]);
  const prefetchRef = useRef<{
    promise: Promise<Record<string, string>>;
    requestedIds: Set<number>;
    isSettled: boolean;
  } | null>(null);
  // Mirrors the modal open state so async continuations can bail out after
  // the user cancels (state captured in closures would be stale)
  const isModalOpenRef = useRef(false);

  const ensureEmployeeQrCodes =
    useEnsureRestaurantCompanyEmployeeQrCodesMutation();

  const startEnsuringTokens = (employeesToPrint: CompanyEmployee[]) => {
    const missingTokenIds = employeesToPrint
      .filter((employee) => !employee.qr_code_token)
      .map((employee) => employee.id);

    if (!missingTokenIds.length) {
      return Promise.resolve<Record<string, string>>({});
    }

    // Reuse a generation that is still in flight (e.g. modal was closed and
    // reopened) instead of POST-ing the same ids again
    const pending = prefetchRef.current;
    if (
      pending &&
      !pending.isSettled &&
      missingTokenIds.every((id) => pending.requestedIds.has(id))
    ) {
      return pending.promise;
    }

    const promise = ensureEmployeeQrCodes.mutateAsync({
      restaurant_id: restaurantId,
      company_id: companyId,
      employee_ids: missingTokenIds,
    });
    const entry = {
      promise,
      requestedIds: new Set(missingTokenIds),
      isSettled: false,
    };
    // The rejection is handled when the promise is awaited on confirm; this
    // catch only prevents an unhandled rejection if the modal is cancelled
    promise.catch(noop).finally(() => {
      entry.isSettled = true;
    });
    promise
      .then((tokens) => {
        setGeneratedTokens((previous) => ({ ...previous, ...tokens }));
      })
      .catch(noop);
    prefetchRef.current = entry;
    return promise;
  };

  const setModalOpen = (open: boolean) => {
    isModalOpenRef.current = open;
    setIsModalOpen(open);
  };

  const openModal = () => {
    const currentEmployees = employees ?? [];
    if (!currentEmployees.length) return;

    snapshotRef.current = currentEmployees;
    startEnsuringTokens(currentEmployees);
    setModalOpen(true);
  };

  const handlePrint = async () => {
    if (isPrinting) return;

    setIsPrinting(true);
    try {
      let generatedTokens: Record<string, string> = {};
      try {
        generatedTokens = await (prefetchRef.current?.promise ??
          Promise.resolve<Record<string, string>>({}));
      } catch (error) {
        // The prefetch from modal open failed - retry once
        generatedTokens = await startEnsuringTokens(snapshotRef.current);
      }

      // The user closed the modal while tokens were being generated
      if (!isModalOpenRef.current) return;

      const employeesWithTokens = [];
      for (const employee of snapshotRef.current) {
        const token = employee.qr_code_token ?? generatedTokens[employee.id];
        if (token) {
          employeesWithTokens.push({ ...employee, qr_code_token: token });
        }
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error(t('common.print-popup-blocked'));
        return;
      }

      try {
        print({
          printWindow,
          content:
            format === 'grid' ? (
              <EmployeeQrCardsPrint employees={employeesWithTokens} />
            ) : (
              <EmployeeQrBusinessCardsPrint employees={employeesWithTokens} />
            ),
        });
      } catch (error) {
        // Don't leave the opened blank window behind if rendering fails
        printWindow.close();
        throw error;
      }
      setModalOpen(false);
    } catch (error) {
      toast.error(t('employee.qr-code-generate-error'));
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            {trigger === 'icon' ? (
              <Button
                variant="ghost"
                disabled={disabled || !employees?.length}
                onClick={openModal}
                className="flex items-center justify-center text-gray-700 p-0"
              >
                <QrCodeIcon className="w-5 h-5" />
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={disabled || !employees?.length}
                onClick={openModal}
                className="print:hidden flex gap-1.5"
              >
                <span>{t('employee.print-qr-codes')}</span>
                <QrCodeIcon className="w-4 h-4" />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {trigger === 'icon'
              ? t('employee.qr-code')
              : t('employee.print-qr-codes-tooltip')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('employee.print-qr-codes')}</DialogTitle>
          </DialogHeader>
          <PrintEmployeeQrCardsModalContent
            employees={snapshotRef.current}
            format={format}
            onFormatChange={setFormat}
            isGenerating={ensureEmployeeQrCodes.isPending}
            generatedTokens={generatedTokens}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            {/* Disabled while generating so the print click is always a fresh
                user gesture - a click after a long await would have its popup
                blocked by the browser */}
            <Button
              isLoading={isPrinting}
              disabled={ensureEmployeeQrCodes.isPending}
              onClick={handlePrint}
            >
              {t('common.print')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
